// Display text for league state.
import { FA_DAYS, PORTAL_DAYS, faDay, portalDay } from '@shared/portal.js';

// type: the league's type ("college" or "pro").
export function phaseLabel(season, type) {
    if (!season) return '';
    switch (season.phase) {
        case 'preseason': return season.year > 1 ? `Season ${season.year} · Preseason` : 'Preseason';
        case 'offseason': return `Offseason · ${offseasonDone(season.offseason?.stage, type)}`;
        case 'regular': return `Regular season · Day ${season.day} of ${season.totalDays}`;
        case 'regular-complete': return 'Regular season complete';
        case 'conference-tournaments': return `Conference tournaments · Day ${season.day}`;
        case 'selection': return 'Selection Day is next';
        case 'tournament': return `National tournament · Day ${season.day}`;
        case 'playoffs': return `Playoffs · Day ${season.day}`;
        case 'complete': return 'Season complete';
        default: return season.phase;
    }
}

// The offseason runs one step a day (stages in @shared/portal.js): where
// the league stands after each step.
function offseasonDone(stage, type) {
    if (stage === 'review') return type === 'pro' ? 'Draft lottery held' : 'Cut window open';
    if (stage === 'cuts') return 'Cut window: last day';
    if (stage === 'portal-open') return `Transfer portal day 1 of ${PORTAL_DAYS}`;
    const day = /^portal-(\d+)$/.exec(stage ?? '')?.[1];
    if (day) return `Transfer portal day ${day} of ${PORTAL_DAYS}`;
    if (stage === 'portal-close') return 'Transfer portal closed';
    if (stage === 'development') return 'Players developed';
    if (stage === 'resign') return 'Re-signing: last day';
    if (stage === 'draft') return 'Draft held';
    if (stage === 'fa-open') return `Free agency day 1 of ${FA_DAYS}`;
    const faDayNum = /^fa-(\d+)$/.exec(stage ?? '')?.[1];
    if (faDayNum) return `Free agency day ${faDayNum} of ${FA_DAYS}`;
    if (stage === 'fa-close') return 'Free agency closed';
    return '';
}

export function inOffseason(season) {
    return season?.phase === 'complete' || season?.phase === 'offseason';
}

// What the next offseason step does, for managers and admins.
export function offseasonNext(league) {
    const season = league?.season;
    const college = league?.type !== 'pro';
    if (season?.phase === 'complete') {
        return 'Next: the season is put in the record books.';
    }
    if (season?.phase !== 'offseason') return null;
    const stage = season.offseason?.stage;
    if (stage === 'review') {
        return college ?
            'Cut window: you can cut up to 3 players before the transfer portal opens.' :
            'The draft lottery is in: rank the prospects in your draft queue. Next: players develop and age, and some retire.';
    }
    if (stage === 'cuts') return 'Last day to cut players. Next: seniors graduate and the transfer portal opens.';
    const day = portalDay(league);
    if (day) {
        return day < PORTAL_DAYS ?
            `The transfer portal is open (day ${day} of ${PORTAL_DAYS}). Pitch players you want; they start committing on day 3.` :
            'Last day of the transfer portal. Next: everyone still in the portal picks a school.';
    }
    if (stage === 'portal-close') return 'Next: players develop and freshmen fill the open spots.';
    if (!college) {
        if (stage === 'development') return 'Re-signing is open: offer your expiring players new contracts (two days).';
        if (stage === 'resign') return 'Last day to re-sign your players and set your draft queue. Next: the draft.';
        if (stage === 'draft') return 'The draft is done. Next: free agency opens.';
        const fa = faDay(league);
        if (fa) {
            return fa < FA_DAYS ?
                `Free agency is open (day ${fa} of ${FA_DAYS}). Make offers and pitches; players start signing on day 3.` :
                'Last day of free agency. Next: everyone with a fair offer signs.';
        }
        if (stage === 'fa-close') return `Next: short rosters are filled and the Season ${season.year + 1} schedule comes out.`;
    }
    if (stage === 'development') return `Next: the Season ${season.year + 1} schedule comes out.`;
    return null;
}

export const POSTSEASON_PHASES =['conference-tournaments', 'selection', 'tournament', 'playoffs', 'complete'];

export function isPostseason(season) {
    return POSTSEASON_PHASES.includes(season?.phase);
}

export function recordText(record) {
    return record ? `${record.w}-${record.l}` : '0-0';
}

export function streakText(streak) {
    if (!streak) return '-';
    return streak > 0 ? `W${streak}` : `L${-streak}`;
}
